import { useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { trackClient } from '@/lib/obs';
import type { RecommendStage } from './recommendCharts';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';

export interface ChartItem {
  id: string;
  name: string;
  icon: string;
  description: string;
  category: string;
  bestUse?: string;
}

export interface ChartTypeShelvesProps {
  charts: ChartItem[];
  onPickChart: (id: string) => void;
  defaultOpenCategory?: string;
  stage: RecommendStage;
  recommendedIds: string[];
  hint?: string;
  onShowAll?: () => void;
}

export default function ChartTypeShelves({
  charts,
  onPickChart,
  stage,
  recommendedIds,
  hint,
  onShowAll,
}: ChartTypeShelvesProps) {
  const recommendedCharts = useMemo(() => {
    if (stage === 'preRoles' || recommendedIds.length === 0) return [];
    return recommendedIds
      .map((id) => charts.find((c) => c.id === id))
      .filter((c): c is ChartItem => c !== undefined);
  }, [charts, recommendedIds, stage]);

  // Pre-roles stage: show hint and optional presets
  if (stage === 'preRoles') {
    return (
      <div className="space-y-3" data-lumi="chart-type-shelves">
        <div data-lumi="recommended-charts">
        <div className="flex items-center justify-between px-2">
          <div className="text-xs font-medium text-foreground">Recommended</div>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-6"
            onClick={onShowAll || (() => {})}
            data-lumi="more-charts"
          >
            Show all
          </Button>
        </div>
        <div className="text-sm text-muted-foreground px-2">
          {hint || 'Pick X and Y to see tailored chart recommendations.'}
        </div>
        </div>
      </div>
    );
  }

  // Show recommended charts
  return (
    <div className="space-y-3" data-lumi="chart-type-shelves">
      <div data-lumi="recommended-charts">
      <div className="flex items-center justify-between px-2">
        <div className="text-xs font-medium text-foreground">Recommended</div>
        <Button
          variant="ghost"
          size="sm"
          className="text-xs h-6"
          onClick={onShowAll || (() => {})}
          data-lumi="more-charts"
        >
          Show all
        </Button>
      </div>
      {recommendedCharts.length > 0 ? (
        <div className="grid grid-cols-2 gap-2 px-2">
          {recommendedCharts.map((chart) => (
            <Card
              key={chart.id}
              className="group h-[72px] p-2 flex items-center gap-2 hover:bg-secondary/50 cursor-pointer border-muted rounded-xl"
              onClick={() => {
                try {
                  trackClient('chart_type_selected', { chart: chart.id });
                } catch {}
                onPickChart(chart.id);
              }}
              aria-label={chart.name}
              data-testid={`chart-card-${chart.id}`}
              data-lumi={`charttype-${chart.id}`}
            >
              <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                <img
                  src={chart.icon + (chart.icon.includes('?') ? '&' : '?') + '_cb=' + ((window as any).__ICON_VERSION__ || Date.now())}
                  alt={chart.name}
                  className="h-full w-full object-contain select-none pointer-events-none"
                  loading="lazy"
                  key={`${chart.id}-${(window as any).__ICON_VERSION__ || '0'}`}
                  onError={(e) => {
                    const el = e.currentTarget as HTMLImageElement;
                    const idx = Number(el.dataset.fallbackIndex || '0');
                    const base = '/chart_icons/';
                    const file = (chart.icon || '').split('/').slice(-1)[0] || '';
                    const stem = file.replace(/\.svg$/i, '');
                    const slug = (s: string, sep: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, sep).replace(new RegExp(`${sep}+$`), '');
                    const candidates = [
                      `${base}${file}`,
                      `${base}${slug(chart.name, '-')}.svg`,
                      `${base}${slug(chart.name, '_')}.svg`,
                      `${base}${slug(chart.name, '')}.svg`,
                      `${base}${slug(stem, '-')}.svg`,
                      `${base}${slug(stem, '_')}.svg`,
                      `${base}${slug(stem, '')}.svg`,
                    ];
                    const next = candidates.filter(u => u && u !== el.src);
                    if (idx < next.length) {
                      el.dataset.fallbackIndex = String(idx + 1);
                      el.src = next[idx];
                    }
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium line-clamp-1">{chart.name}</div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="px-2 py-6 text-center space-y-2">
          <div className="text-sm font-medium text-foreground">Select data to see chart recommendations.</div>
          <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <span>Chart Builder is optional. You can describe your chart in chat.</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    aria-label="More information about Chart Builder"
                  >
                    <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[280px]">
                  <div className="space-y-2">
                    <div className="font-medium text-xs">Prefer typing?</div>
                    <div className="text-[11px] text-muted-foreground space-y-1.5">
                      <p>
                        Add more stats, filters, chart types, annotations, N labels, colour palettes, or other custom details in the chat box below to refine your plot.
                      </p>
                      <div className="pt-1 space-y-0.5">
                        <p className="text-[10px] italic">Example: &quot;line chart of Y vs X, group by Series&quot;</p>
                        <p className="text-[10px] italic">Example: &quot;add CI and show N&quot;</p>
                      </div>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
